import React from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Nivo — /login (Phase 10.1 redesign, Feb 2026).
//
// Layout & design:
//   • Full-viewport split (58% brand / 42% form on desktop, single column on mobile).
//   • Left panel: hand-tuned aurora mesh (multiple radial gradients + soft blur
//     blobs + SVG noise). Feels premium and modern — inspired by Linear / Attio
//     brand panels rather than a flat solid colour.
//   • Right panel: white, LEFT-aligned form composition (not centred stack),
//     with a small tracked eyebrow ("AUTHENTIFICATION"), a large 40px bold
//     welcome, and a Google button styled like Attio / Linear — white with a
//     1 px border and a soft hover lift. This reads more professional than a
//     coloured gradient CTA in 2026 SaaS logins.
//   • Trust reassurance line under the button ("Connexion sécurisée · Google
//     OAuth 2.0") using an outline lock icon.
//   • Footer collapsed to a single tiny copyright anchored bottom-right of
//     the right panel — no full-width bar, no visual noise.
//   • Staggered fade-in entrance for the right panel elements.
//   • Height locked to h-screen so nothing scrolls on desktop.
//
// AUTH LOGIC IS COMPLETELY UNCHANGED. Do NOT hardcode URLs, add fallbacks, or
// bypass the backend redirect — everything server-side stays exactly the same.

// French messages for each backend-issued OAuth error code. `not_whitelisted`
// MUST remain exactly "Accès refusé. Contactez votre administrateur."
const ERROR_MESSAGES = {
    not_whitelisted: 'Accès refusé. Contactez votre administrateur.',
    oauth_not_configured: "L'authentification Google n'est pas encore configurée. Contactez votre administrateur.",
    state_mismatch: 'Session expirée pendant la connexion. Réessayez.',
    token_exchange_failed: "Échec de l'authentification Google. Réessayez.",
    id_token_invalid: "Réponse Google invalide. Réessayez.",
    google_denied: "Connexion Google annulée.",
};

const GoogleIcon = ({ className = 'h-5 w-5' }) => (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.418 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C33.64 6.053 29.06 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
        <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C33.64 6.053 29.06 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
        <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.397 0-9.939-3.077-11.283-7.383l-6.5 5.02C9.5 39.556 16.227 44 24 44z"/>
        <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
);

// Lock icon — inline outline SVG so we don't pull the whole lucide bundle.
const LockIcon = ({ className = 'h-3.5 w-3.5' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

function readErrorFromQuery() {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('error');
    if (!code) return null;
    return { code, message: ERROR_MESSAGES[code] || "Échec de l'authentification. Réessayez." };
}

// Aurora mesh — reusable inline background style. Layered radial gradients on
// a deep indigo base + a hint of magenta / violet for depth.
const AURORA_BG = {
    background: [
        // Very soft top-left halo
        'radial-gradient(ellipse 55% 60% at 12% 8%, rgba(155, 127, 244, 0.55) 0%, rgba(155, 127, 244, 0) 60%)',
        // Warm coral hint top-right (gives it that Attio-esque warmth)
        'radial-gradient(ellipse 45% 50% at 92% 18%, rgba(236, 118, 176, 0.32) 0%, rgba(236, 118, 176, 0) 55%)',
        // Cyan cool tint centre-right
        'radial-gradient(ellipse 55% 45% at 100% 55%, rgba(94, 132, 234, 0.35) 0%, rgba(94, 132, 234, 0) 60%)',
        // Deep indigo pool bottom-left
        'radial-gradient(ellipse 60% 55% at 20% 100%, rgba(75, 63, 216, 0.85) 0%, rgba(75, 63, 216, 0) 60%)',
        // Base darker indigo
        'linear-gradient(160deg, #1E1668 0%, #2C1F8F 45%, #4B3FD8 100%)',
    ].join(', '),
};

// SVG noise / grain — very subtle (3% opacity) fractal turbulence overlay.
// Fixed pixel size to avoid re-rendering on resize.
const NoiseLayer = () => (
    <svg
        aria-hidden="true"
        className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.06] mix-blend-overlay"
        xmlns="http://www.w3.org/2000/svg"
    >
        <filter id="nivo-grain">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#nivo-grain)" />
    </svg>
);

export default function LoginPage() {
    const [rejection] = React.useState(readErrorFromQuery);
    const [buttonHover, setButtonHover] = React.useState(false);

    const handleGoogleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        window.location.href = `${BACKEND_URL}/api/auth/google/login`;
    };

    return (
        <main
            data-testid="login-page"
            className="h-screen w-full flex bg-white overflow-hidden font-sans text-[#1A1D2E]"
        >
            {/* ==================== LEFT PANEL — brand aurora ==================== */}
            <aside
                data-testid="login-left-panel"
                className="hidden md:flex md:w-[58%] relative overflow-hidden"
                style={AURORA_BG}
            >
                {/* Softly blurred colour blobs — pure decoration, non-interactive. */}
                <div
                    aria-hidden="true"
                    className="absolute -top-24 -left-24 w-[540px] h-[540px] rounded-full opacity-50"
                    style={{ background: 'radial-gradient(circle, #A390FF 0%, transparent 65%)', filter: 'blur(90px)' }}
                />
                <div
                    aria-hidden="true"
                    className="absolute -bottom-40 -right-20 w-[620px] h-[620px] rounded-full opacity-40"
                    style={{ background: 'radial-gradient(circle, #6E5AE8 0%, transparent 65%)', filter: 'blur(110px)' }}
                />
                <div
                    aria-hidden="true"
                    className="absolute top-1/3 right-1/4 w-[320px] h-[320px] rounded-full opacity-30"
                    style={{ background: 'radial-gradient(circle, #EC76B0 0%, transparent 70%)', filter: 'blur(80px)' }}
                />

                {/* Grain layer */}
                <NoiseLayer />

                {/* Content grid: top-left lockup / centre statement / bottom-left status */}
                <div className="relative z-10 flex-1 flex flex-col justify-between p-12 lg:p-14">
                    {/* Top row — Nivo lockup only (dropped the CRM pill; the lockup speaks for itself) */}
                    <div>
                        <img
                            src="/logos/nivo-lockup.png"
                            alt="Nivo"
                            data-testid="login-left-logo"
                            className="h-9 w-auto select-none"
                            draggable={false}
                            style={{ filter: 'brightness(0) invert(1)' }}
                        />
                    </div>

                    {/* Centre statement — client-provided quote (Phase 10.2, Feb 2026).
                        Two lines only: a big headline sentence, then a warmer sub. The
                        eyebrow was removed at the client's request — no replacement text. */}
                    <div className="max-w-[520px] animate-fade-in-up" style={{ animationDelay: '80ms' }}>
                        <h2
                            data-testid="login-left-headline"
                            className="text-white text-[40px] lg:text-[46px] font-extrabold leading-[1.1] tracking-[-0.02em]"
                        >
                            Les meilleurs résultats viennent des équipes qui restent concentrées.
                        </h2>
                        <p
                            data-testid="login-left-subline"
                            className="mt-8 text-white/70 text-[15px] leading-relaxed max-w-[420px]"
                        >
                            Belle journée à toi. Nous avons confiance en toi et en ton travail.
                        </p>
                    </div>

                    {/* Bottom row — a single status marker, no city list */}
                    <div className="flex items-center gap-2 text-white/60 text-[12px] tracking-wide">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span>Tous services opérationnels</span>
                    </div>
                </div>
            </aside>

            {/* ==================== RIGHT PANEL — form ==================== */}
            <section
                data-testid="login-right-panel"
                className="flex-1 md:w-[42%] relative flex items-center justify-center px-6 md:px-14 py-10 bg-white"
            >
                <div className="w-full max-w-[380px] animate-fade-in-up">
                    {/* Mobile-only mark (the left panel is hidden below md). Sits above the form. */}
                    <img
                        src="/logos/nivo-mark.png"
                        alt="Nivo"
                        data-testid="login-right-mark"
                        className="md:hidden h-16 w-auto mb-10 select-none"
                        draggable={false}
                    />

                    {/* Tracked eyebrow */}
                    <p
                        data-testid="login-eyebrow"
                        className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4B3FD8]"
                    >
                        Authentification
                    </p>

                    {/* Big welcome */}
                    <h1
                        data-testid="login-title"
                        className="mt-3 text-[40px] font-extrabold leading-[1.05] tracking-[-0.02em] text-[#1A1D2E]"
                    >
                        Bienvenue.
                    </h1>

                    {/* Subline */}
                    <p
                        data-testid="login-subtitle"
                        className="mt-4 text-[15px] leading-relaxed text-[#6B7280] max-w-[340px]"
                    >
                        Connectez-vous avec le compte Google associé à votre organisation. L'accès est limité aux utilisateurs autorisés.
                    </p>

                    {/* Google button — Attio/Linear-style: white, 1 px border, gentle lift on hover. */}
                    <button
                        data-testid="login-google-button"
                        type="button"
                        onClick={handleGoogleLogin}
                        onMouseEnter={() => setButtonHover(true)}
                        onMouseLeave={() => setButtonHover(false)}
                        className="mt-10 group relative inline-flex items-center justify-center gap-3 w-full h-[52px] rounded-[10px] bg-white text-[15px] font-semibold text-[#1A1D2E] border border-[#E5E7EB] hover:border-[#C7C1F2] transition-all duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4B3FD8]/25"
                        style={{
                            boxShadow: buttonHover
                                ? '0 1px 2px rgba(16,24,40,0.06), 0 12px 24px -12px rgba(75,63,216,0.35), 0 4px 8px -4px rgba(75,63,216,0.15)'
                                : '0 1px 2px rgba(16,24,40,0.05)',
                            transform: buttonHover ? 'translateY(-1px)' : 'translateY(0)',
                        }}
                    >
                        <GoogleIcon className="h-5 w-5" />
                        <span>Continuer avec Google</span>
                        <svg className={`h-4 w-4 text-[#6B7280] transition-transform duration-200 ${buttonHover ? 'translate-x-0.5' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </button>

                    {/* Persistent rejection message — sits directly below the button. */}
                    {rejection && (
                        <div
                            data-testid="login-error"
                            data-error-code={rejection.code}
                            role="alert"
                            className="mt-4 flex items-start gap-2.5 rounded-[8px] border border-[#EF4444]/25 bg-[#EF4444]/[0.06] px-3.5 py-3 text-[13px] leading-snug text-[#B91C1C]"
                        >
                            <svg className="mt-[1px] h-4 w-4 shrink-0 text-[#EF4444]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span className="font-medium">{rejection.message}</span>
                        </div>
                    )}

                    {/* Trust reassurance chip — sits alone under the button (no extra divider,
                        no duplicate "contact admin" line: the not_whitelisted error already
                        carries that copy verbatim). */}
                    <div className="mt-6 flex items-center gap-2 text-[12px] text-[#9CA3AF]">
                        <LockIcon className="h-3.5 w-3.5 text-[#9CA3AF]" />
                        <span>Connexion sécurisée · Google OAuth 2.0</span>
                    </div>
                </div>

                {/* Discreet copyright, absolute bottom-right of the right panel. */}
                <div
                    data-testid="login-footer"
                    className="absolute bottom-5 right-6 text-[11px] text-[#9CA3AF] tracking-wide select-none"
                >
                    © 2026 SH Power Group
                </div>
            </section>
        </main>
    );
}
